import { Session } from '@/types'
import { formatDate, formatTime } from '@/lib/utils'

// Helper function to escape CSV fields
const escapeCSVField = (field: any): string => {
  if (field === null || field === undefined) return ''
  const str = String(field)
  // Escape quotes and wrap in quotes if contains comma, quote, or newline
  if (str.includes('"') || str.includes(',') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

// Helper function to format date for CSV
const formatDateForCSV = (dateString: string | null | undefined): string => {
  if (!dateString) return ''
  try {
    return formatDate(dateString)
  } catch {
    return ''
  }
}

// Helper function to format time for CSV
const formatTimeForCSV = (dateString: string | null | undefined): string => {
  if (!dateString) return ''
  try {
    return formatTime(dateString)
  } catch {
    return ''
  }
}

export const generateCSV = (sessions: Session[]): string => {
  // Define CSV headers
  const headers = [
    'Session ID',
    'Student Name',
    'Counselor Name',
    'Agency Name',
    'Type',
    'Status',
    'Medium',
    'Started Date',
    'Started Time',
    'Ended Date',
    'Ended Time',
    'Duration (minutes)',
    'Rating',
    'Feedback',
    'Summary',
    'Notes',
    'Amount',
    'Created At',
  ]

  // Generate CSV rows
  const rows = sessions.map(session => [
    escapeCSVField(session.id),
    escapeCSVField(session.studentName),
    escapeCSVField(session.counselorName),
    escapeCSVField(session.agencyName || ''),
    escapeCSVField((session.type || '').replace(/_/g, ' ')),
    escapeCSVField((session.status || '').replace(/_/g, ' ')),
    escapeCSVField(session.medium || ''),
    escapeCSVField(formatDateForCSV(session.startedAt)),
    escapeCSVField(formatTimeForCSV(session.startedAt)),
    escapeCSVField(formatDateForCSV(session.endedAt)),
    escapeCSVField(formatTimeForCSV(session.endedAt)),
    escapeCSVField(session.duration),
    escapeCSVField(session.rating || ''),
    escapeCSVField(session.feedback || ''),
    escapeCSVField(session.summary || ''),
    escapeCSVField(session.notes || ''),
    escapeCSVField(session.amount || ''),
    escapeCSVField(formatDateForCSV(session.createdAt)),
  ])

  // Combine headers and rows
  const csvContent = [headers, ...rows]
    .map(row => row.join(','))
    .join('\n')

  return csvContent
}

export const downloadCSV = (csvContent: string, filename?: string): void => {
  // Create filename with current date if not provided
  const currentDate = new Date().toISOString().split('T')[0]
  const finalFilename = filename || `sessions_export_${currentDate}.csv`

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', finalFilename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  } else {
    // Fallback for older browsers
    window.open('data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent))
  }
}

export const exportSessionsToCSV = (sessions: Session[], filename?: string): void => {
  try {
    const csvContent = generateCSV(sessions)
    downloadCSV(csvContent, filename)
  } catch (error) {
    console.error('Error exporting sessions to CSV:', error)
    throw new Error('Failed to export sessions to CSV')
  }
}
